import React, { useState } from 'react';
import { AthleteProfile, SportType } from '../types';
import { createFreshAthleteProfile } from '../services/firestoreService';
import { auth } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { sendVerificationEmailUtility } from '../utils/authUtils';
import { X, UserPlus, Camera, Sparkles, Lock, Mail, User, Loader2 } from 'lucide-react';
import { StateCitySelector } from './common/StateCitySelector';

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthInitializing?: boolean;
  onRegisterUser: (newUser: AthleteProfile) => void;
  onSwitchToLogin: () => void;
}

export const SignUpModal: React.FC<SignUpModalProps> = ({
  isOpen,
  onClose,
  isAuthInitializing = false,
  onRegisterUser,
  onSwitchToLogin,
}) => {
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [primarySport, setPrimarySport] = useState<SportType>('basketball');
  const [role, setRole] = useState<'high_school' | 'college' | 'playground_pro' | 'scout_recruiter'>('high_school');
  const [schoolOrLeague, setSchoolOrLeague] = useState('');
  const [position, setPosition] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('7');
  const [registeredState, setRegisteredState] = useState('NY');
  const [registeredCity, setRegisteredCity] = useState('New York');
  const [bio, setBio] = useState('');
  const [avatar, setAvatar] = useState('https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300');
  const [referralCodeInput, setReferralCodeInput] = useState('');

  React.useEffect(() => {
    try {
      const savedRef = localStorage.getItem('playground_pending_ref_code');
      if (savedRef) {
        setReferralCodeInput(savedRef);
      }
    } catch (e) {}
  }, [isOpen]);

  const presetAvatars = [
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=300',
  ];

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    const formattedHandle = handle.startsWith('@') ? handle : `@${handle.trim() || name.toLowerCase().replace(/\s+/g, '')}`;

    const newUserProfile = createFreshAthleteProfile({
      id: `user_registered_${Date.now()}`,
      name: name.trim(),
      handle: formattedHandle,
      email: email.trim(),
      avatar,
      role,
      schoolOrLeague: schoolOrLeague.trim() || (role === 'scout_recruiter' ? 'NCAA / FIBA Talent Agency' : 'Playground League'),
      primarySport,
      position: position.trim() || (role === 'scout_recruiter' ? 'Certified Scout' : 'All-Around'),
      jerseyNumber: Number(jerseyNumber) || 7,
      registeredState: registeredState.trim() || 'NY',
      registeredCity: registeredCity.trim() || 'New York',
      hasCompletedLocationOnboarding: false,
      bio: bio.trim() || (role === 'scout_recruiter' ? 'Verified Talent Evaluator looking for top D1/FIBA prospects.' : 'New registered athlete on Playgrounds platform. Ready to compete and log stats!'),
    });

    newUserProfile.emailVerified = false;

    if (referralCodeInput.trim()) {
      newUserProfile.xp = (newUserProfile.xp || 100) + 100;
      newUserProfile.referralCodeUsed = referralCodeInput.trim();
      try {
        localStorage.removeItem('playground_pending_ref_code');
      } catch (e) {}
    }

    if (role === 'scout_recruiter') {
      newUserProfile.isScout = true;
      newUserProfile.isVerifiedScout = false;
      newUserProfile.scoutPassActive = false;
      newUserProfile.scoutOrgName = schoolOrLeague.trim() || 'NCAA / FIBA Scouting Network';
    }

    // Dispatch email verification link and 6-digit OTP security code
    sendVerificationEmailUtility(email.trim(), name.trim()).catch((err) => {
      console.warn('sendVerificationEmailUtility on signup notice:', err);
    });

    // Dispatch automatic welcome email via backend API
    fetch('/api/email/welcome', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        name: name.trim(),
        sport: primarySport,
        isPro: role === 'playground_pro',
      }),
    }).catch((err) => console.warn('Welcome email call background notice:', err));

    onRegisterUser(newUserProfile);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-indigo-900/90 text-white rounded-[2.5rem] border border-white/15 shadow-2xl w-full max-w-lg overflow-hidden p-6 my-8 relative space-y-5">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center space-y-1">
          <div className="w-12 h-12 rounded-2xl bg-lime-400 text-black mx-auto flex items-center justify-center font-black text-xl shadow-lg shadow-lime-400/20">
            <UserPlus className="w-6 h-6 stroke-[2.5]" />
          </div>
          <h2 className="text-2xl font-black italic uppercase tracking-tight text-white pt-2">
            Register Athlete Profile
          </h2>
          <p className="text-xs text-indigo-200/70 font-semibold">
            Create your public account to start logging stats, joining pickup runs, and climbing leaderboards.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Upload */}
          <div className="flex items-center space-x-4 bg-indigo-950/60 p-3.5 rounded-2xl border border-white/10">
            <img
              src={avatar}
              alt="Avatar preview"
              className="w-14 h-14 rounded-2xl object-cover border-2 border-lime-400 shrink-0"
              referrerPolicy="no-referrer"
            />

            <div className="flex-1 space-y-1.5">
              <span className="text-xs font-bold text-indigo-200 block">Profile Picture / Avatar</span>
              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-black rounded-xl text-xs font-black italic uppercase flex items-center space-x-1 transition shadow-md">
                  <Camera className="w-3.5 h-3.5" />
                  <span>Upload Pic</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          if (event.target?.result) {
                            setAvatar(event.target.result as string);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>

                <span className="text-[10px] text-indigo-200/60 font-semibold">Presets:</span>
                {presetAvatars.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setAvatar(url)}
                    className={`w-7 h-7 rounded-lg overflow-hidden border transition ${
                      avatar === url ? 'border-lime-400 scale-110' : 'border-white/20 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={url} alt="preset" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Full Name & Handle */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Full Name *</label>
              <div className="relative">
                <User className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                <input
                  type="text"
                  required
                  placeholder="e.g. Alex Morgan"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">User Handle *</label>
              <div className="relative">
                <span className="text-xs font-black text-indigo-300 absolute left-3 top-2.5">@</span>
                <input
                  type="text"
                  required
                  placeholder="alex_m10"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  className="w-full pl-7 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>
          </div>

          {/* Email & Password */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Email Address *</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                <input
                  type="email"
                  required
                  placeholder="alex@playgrounds.app"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Password *</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>
          </div>

          {/* Primary Sport & Role Level */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Primary Sport</label>
              <select
                value={primarySport}
                onChange={(e) => setPrimarySport(e.target.value as SportType)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="basketball">Basketball 🏀</option>
                <option value="baseball">Baseball ⚾</option>
                <option value="softball">Softball 🥎</option>
                <option value="pickleball">Pickleball 🏓</option>
                <option value="soccer">Soccer ⚽</option>
                <option value="volleyball">Volleyball 🏐</option>
                <option value="football">Football 🏈</option>
                <option value="tennis">Tennis 🎾</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">League Bracket</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="high_school">High School Varsity</option>
                <option value="college">College NCAA</option>
                <option value="playground_pro">Playground Pro / Rec</option>
                <option value="scout_recruiter">College / FIBA Scout (Recruiter Pass)</option>
              </select>
            </div>
          </div>

          {role === 'scout_recruiter' && (
            <div className="p-3 bg-amber-500/15 border border-amber-500/30 rounded-2xl text-xs text-amber-200 font-semibold space-y-1 animate-fadeIn">
              <span className="font-bold text-amber-300 block">👁️ Recruiter Pass Required for Scout Access</span>
              <p className="text-[11px] text-amber-100/80">
                Scout accounts allow searching player databases and downloading game reels. Unredacted athlete contact sheets require the <strong>Recruiter Pass ($49/mo)</strong>.
              </p>
            </div>
          )}

          {/* School/League & Position & Jersey */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="text-xs font-bold text-indigo-200 block mb-1">School / League</label>
              <input
                type="text"
                placeholder="e.g. West High"
                value={schoolOrLeague}
                onChange={(e) => setSchoolOrLeague(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div className="col-span-1">
              <label className="text-xs font-bold text-indigo-200 block mb-1">Position</label>
              <input
                type="text"
                placeholder="e.g. QB / PG"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div className="col-span-1">
              <label className="text-xs font-bold text-indigo-200 block mb-1">Jersey #</label>
              <input
                type="number"
                placeholder="7"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
          </div>

          {/* Regional State & City Registration Fields */}
          <StateCitySelector
            selectedState={registeredState}
            selectedCity={registeredCity}
            onStateChange={(newState, defaultCity) => {
              setRegisteredState(newState);
              setRegisteredCity(defaultCity);
            }}
            onCityChange={(newCity) => setRegisteredCity(newCity)}
          />

          <div className="p-3 bg-indigo-950/80 rounded-xl border border-white/10 text-[11px] text-indigo-200/90 flex items-center space-x-2">
            <Mail className="w-4 h-4 text-lime-400 shrink-0" />
            <span>
              A confirmation email will be sent to your inbox from <strong className="text-lime-300">support@playgroundleague.pro</strong>.
            </span>
          </div>

          {/* Submit Registration */}
          <button
            type="submit"
            disabled={isAuthInitializing}
            className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition mt-2"
          >
            {isAuthInitializing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Checking Auth & Creating Profile...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 stroke-[2.5]" />
                <span>Complete Registration & Launch Profile</span>
              </>
            )}
          </button>

          {/* Switch to SSO / Login option */}
          <div className="pt-2 text-center text-xs text-indigo-200/70 font-semibold">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => {
                onClose();
                onSwitchToLogin();
              }}
              className="text-lime-400 hover:underline font-bold"
            >
              Log in or switch SSO account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
